/**
 * Article 90 headroom widget — M3X.LOANS PR2 (F.6 keystone).
 *
 * Renders Mutaba'aa's per-employee Article 90 aggregate-deduction
 * snapshot: monthly wage, max allowable (1/3), existing deductions
 * breakdown, headroom remaining, utilization percent + bar.
 *
 * Used by:
 *   - /me/loans (employee self-service — PR4)
 *   - Loan request wizard (PR5 — eligibility check with live headroom)
 *   - HR loan detail page (PR1 / PR6 — §4.7 LoanDetailPage)
 *   - Future Khidmat chat replies (PR19)
 *
 * Agent attribution: `<AgentBadge agent="mutabaa">` per Commandment 65.
 * Bilingual EN+AR per Commandment 37. RTL-correct via logical properties.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api } from "../lib/api";
import { ErrorPanel } from "./Loading";
import { AgentBadge } from "./ui/AgentBadge";
import { Skeleton } from "./ui/Skeleton";

interface Props {
  employeeId: string;
  asOf?: string;
}

export interface Article90HeadroomDTO {
  employee_id: string;
  as_of: string;
  monthly_basic_wage: string;
  max_allowable_monthly_deduction: string;
  existing_deductions_breakdown: {
    source: string;
    amount: string;
    rule_citation: string;
  }[];
  existing_deductions_total: string;
  headroom_remaining: string;
  utilization_percent: string;
  agent_attribution: string;
  article_citation: string;
}

function utilizationTone(percentStr: string): "ok" | "warn" | "crit" {
  const p = Number.parseFloat(percentStr);
  if (Number.isNaN(p)) return "ok";
  if (p >= 30) return "crit";
  if (p >= 20) return "warn";
  return "ok";
}

function toneClasses(t: "ok" | "warn" | "crit") {
  if (t === "crit") {
    return {
      bar: "bg-rose-500",
      bg: "bg-rose-50 border-rose-200",
      pill: "bg-rose-100 text-rose-800",
    };
  }
  if (t === "warn") {
    return {
      bar: "bg-amber-500",
      bg: "bg-amber-50 border-amber-200",
      pill: "bg-amber-100 text-amber-800",
    };
  }
  return {
    bar: "bg-emerald-500",
    bg: "bg-emerald-50 border-emerald-200",
    pill: "bg-emerald-100 text-emerald-800",
  };
}

export function Article90HeadroomWidget({ employeeId, asOf }: Props) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";

  const { data, isLoading, error } = useQuery({
    queryKey: ["article-90-headroom", employeeId, asOf],
    queryFn: () => api.getArticle90Headroom(employeeId, asOf),
  });

  if (isLoading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <Skeleton className="h-4 w-1/3 mb-3" />
        <Skeleton className="h-2 w-full mb-2" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <ErrorPanel
        message={t(
          "loans.article_90_widget_error",
          "Couldn't load Article 90 headroom.",
        )}
      />
    );
  }

  const tone = utilizationTone(data.utilization_percent);
  const classes = toneClasses(tone);
  const utilPct = Math.min(
    100,
    Math.max(0, Number.parseFloat(data.utilization_percent) || 0),
  );

  return (
    <div className={`rounded-lg border p-4 ${classes.bg}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AgentBadge agent="mutabaa" />
          <h3 className="font-semibold text-base">
            {t(
              "loans.article_90_widget_title",
              "Article 90 — Deduction Headroom",
            )}
          </h3>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full ${classes.pill}`}>
          {data.utilization_percent}%{" "}
          {t("loans.article_90_widget_used", "used")}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm mb-3">
        <div>
          <div className="text-text-tertiary text-xs">
            {t("loans.article_90_monthly_wage", "Monthly basic wage")}
          </div>
          <div className="font-mono font-semibold">
            {data.monthly_basic_wage} SAR
          </div>
        </div>
        <div>
          <div className="text-text-tertiary text-xs">
            {t(
              "loans.article_90_max_allowable",
              "Max allowable (1/3 of wage)",
            )}
          </div>
          <div className="font-mono font-semibold">
            {data.max_allowable_monthly_deduction} SAR
          </div>
        </div>
        <div>
          <div className="text-text-tertiary text-xs">
            {t(
              "loans.article_90_existing_total",
              "Existing deductions total",
            )}
          </div>
          <div className="font-mono font-semibold">
            {data.existing_deductions_total} SAR
          </div>
        </div>
        <div>
          <div className="text-text-tertiary text-xs">
            {t("loans.article_90_headroom_remaining", "Headroom remaining")}
          </div>
          <div className="font-mono font-semibold">
            {data.headroom_remaining} SAR
          </div>
        </div>
      </div>

      <div
        className="h-2 bg-white border border-slate-300 rounded-full overflow-hidden"
        role="progressbar"
        aria-valuenow={utilPct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={`h-full ${classes.bar}`}
          style={{ width: `${utilPct}%` }}
        />
      </div>

      {data.existing_deductions_breakdown.length > 0 ? (
        <details className="mt-3 text-xs text-text-secondary">
          <summary className="cursor-pointer hover:text-text-primary">
            {t(
              "loans.article_90_breakdown_summary",
              "View deduction breakdown",
            )}
          </summary>
          <ul className="mt-2 space-y-1">
            {data.existing_deductions_breakdown.map((d) => (
              <li
                key={d.source}
                className="flex justify-between gap-2 border-b border-slate-200 pb-1"
              >
                <span>
                  <span className="font-mono">{d.source}</span>
                  <span className="text-text-tertiary ms-1">
                    ({d.rule_citation})
                  </span>
                </span>
                <span className="font-mono">{d.amount} SAR</span>
              </li>
            ))}
          </ul>
        </details>
      ) : (
        <div className="mt-3 text-xs text-text-tertiary">
          {t(
            "loans.article_90_no_deductions",
            "No active deductions on record.",
          )}
        </div>
      )}

      <div className="mt-3 text-xs text-text-tertiary">
        {isAr
          ? `سقف المادة 90 — يفرضه ${data.agent_attribution} (متابعة)`
          : `Article 90 cap — enforced by ${data.agent_attribution}`}
      </div>
    </div>
  );
}
